import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";

type FooterLink = { name: string; target: string; icon?: ReactNode; ariaLabel?: string };

type FooterGroup = {
  title: string;
  links: FooterLink[];
};

type FooterProps = {
  background?: "DarkGreen" | "Light";
  NavLinks?: boolean;
  groupLinks?: FooterGroup[];
  socialMediaTitle?: string;
  accessibilityTitle?: string;
  socialMediaLinks?: FooterLink[];
  accessibilityLinks?: FooterLink[];
  copyright?: string;
  basicLinks?: FooterLink[];
  extraLinks?: FooterLink[];
  bottomImages?: string[];
  lastUpdated?: string;
};

const palette = {
  DarkGreen: "bg-[#074D31] text-white",
  Light: "bg-gray-50 text-gray-900",
};

export default function DgaFooter({
  background = "DarkGreen",
  NavLinks = true,
  groupLinks = [],
  socialMediaTitle,
  accessibilityTitle,
  socialMediaLinks = [],
  accessibilityLinks = [],
  copyright,
  basicLinks = [],
  extraLinks = [],
  bottomImages = [],
  lastUpdated,
}: FooterProps) {
  const { t, isRTL } = useLanguage();
  const hasNav = NavLinks && groupLinks.length > 0;
  const bgClasses = palette[background] ?? palette.DarkGreen;
  const isDark = background === "DarkGreen";
  const textMuted = isDark ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900";
  const divider = isDark ? "border-white/10" : "border-gray-200";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const iconButtonColor = isDark ? "text-white hover:text-white" : "text-gray-600 hover:text-gray-900";
  const isInternalTarget = (target?: string) => !!target && target.startsWith("/");

  return (
    <footer className={`${bgClasses} pt-14 pb-10`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 space-y-10">
        {hasNav && (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 ${isRTL ? '' : 'flex-row-reverse'}`}>
          <div className="lg:col-span-3">
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 ${isRTL ? '' : 'flex-row-reverse'}`}>
                {groupLinks.map((group) => (
                  <div key={group.title}>
                    <h3 className={`text-lg font-semibold mb-3 pb-2 border-b ${divider} ${headingColor}`}>{group.title}</h3>
                    <ul className="space-y-2">
                      {group.links.map((link) => (
                        <li key={link.name}>
                          {isInternalTarget(link.target) ? (
                            <Link
                              to={link.target}
                              className={`${textMuted} transition-colors duration-200 inline-flex items-center gap-2`}
                            >
                              {link.name}
                            </Link>
                          ) : (
                            <a
                              href={link.target || "#"}
                              className={`${textMuted} transition-colors duration-200 inline-flex items-center gap-2`}
                            >
                              {link.name}
                            </a>
                          )}
                    </li>
                      ))}
              </ul>
            </div>
                ))}
              </div>
            </div>

            {(socialMediaLinks.length > 0 || accessibilityLinks.length > 0) && (
              <div className="space-y-8">
                {socialMediaLinks.length > 0 && (
            <div>
                    {socialMediaTitle && <h3 className={`text-lg font-semibold mb-4 ${headingColor}`}>{socialMediaTitle}</h3>}
                    <div className="flex flex-wrap justify-start gap-3">
                      {socialMediaLinks.map((link) => (
                        <a
                          key={link.name}
                          href={link.target || "#"}
                          className={`w-11 h-11 rounded-md border ${divider} flex items-center justify-center ${iconButtonColor} transition-all duration-200 hover:-translate-y-0.5`}
                          aria-label={link.ariaLabel || link.name}
                        >
                          {link.icon ?? <span className="text-xs font-medium">{link.name}</span>}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {accessibilityLinks.length > 0 && (
                  <div>
                    {accessibilityTitle && <h3 className={`text-lg font-semibold mb-4 ${headingColor}`}>{accessibilityTitle}</h3>}
                    <div className="flex flex-wrap justify-start gap-3">
                      {accessibilityLinks.map((link) => (
                        <a
                          key={link.name}
                          href={link.target || "#"}
                          className={`w-11 h-11 rounded-md border ${divider} flex items-center justify-center ${iconButtonColor} transition-all duration-200 hover:-translate-y-0.5`}
                          aria-label={link.ariaLabel || link.name}
                        >
                          {link.icon ?? <span className="text-xs font-medium">{link.name}</span>}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {(basicLinks.length > 0 || extraLinks.length > 0) && (
          <div className={`flex flex-col md:flex-row justify-between gap-6 border-t ${divider} pt-6 ${isRTL ? '' : 'flex-row-reverse'}`}>
            {basicLinks.length > 0 && (
              <div className="flex flex-wrap gap-4 text-sm font-medium">
                {basicLinks.map((link) => (
                  isInternalTarget(link.target) ? (
                    <Link key={link.name} to={link.target} className={textMuted}>
                      {link.name}
                    </Link>
                  ) : (
                    <a key={link.name} href={link.target || "#"} className={textMuted}>
                      {link.name}
                    </a>
                  )
                ))}
        </div>
            )}

            {extraLinks.length > 0 && (
              <div className="text-sm space-y-1">
                {extraLinks.map((link) => (
                  isInternalTarget(link.target) ? (
                    <Link key={link.name} to={link.target} className={textMuted}>
                      {link.name}
                    </Link>
                  ) : (
                    <a key={link.name} href={link.target || "#"} className={textMuted}>
                      {link.name}
                    </a>
                  )
                ))}
              </div>
            )}
          </div>
        )}

        <div className={`flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 border-t ${divider} pt-6 ${isRTL ? '' : 'flex-row-reverse'}`}>
          <div className={`space-y-1 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
            {copyright && <p className="font-medium">{copyright}</p>}
            {!copyright && (
              <p className={textMuted}>{t('footerAllRightsReservedDGA')}</p>
            )}
            {lastUpdated && <p className={textMuted}>{lastUpdated}</p>}
          </div>

          {bottomImages.length > 0 && (
            <div className="flex items-center gap-4">
              {bottomImages.map((src, idx) => (
                <img key={`${src}-${idx}`} src={src} alt="" className="h-[150px] w-auto object-contain" />
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
